// ============================================
// 镜界 LinkSight - 代理商管理系统
// 基于 LinkWave 灵波设计系统
// ============================================

import React, { useState } from 'react';
import { 
  Store, FileText, ShoppingCart, MapPin, 
  Bell, Search, Zap, BarChart3, Map, Settings,
  Plus, Phone, DollarSign, Package, Clock,
  Download, TrendingUp, TrendingDown,
  ShoppingBag, AlertCircle,
  ArrowUpRight, ArrowDownRight, Eye, MessageSquare,
  Truck, Award, Target
} from 'lucide-react';
import { OpenClawModule } from './OpenClawModule';
import { LocationScoutModule } from './LocationScoutModule';
import { ClientMapModule } from './ClientMapModule';

// ==================== 数据 ====================
const AGENT_INFO = {
  name: '代理商工作台',
  region: '区域管理中心',
  brands: [
    { id: 'wx', name: '万新光学', logo: 'WX' },
    { id: 'rd', name: '罗敦司得', logo: 'RD' },
  ],
};

const MODULES = [
  { id: 'dashboard', label: '经营总览', icon: BarChart3 },
  { id: 'stores', label: '门店管理', icon: Store, badge: 12 },
  { id: 'contracts', label: '合同协议', icon: FileText, badge: 8 },
  { id: 'orders', label: '订单管理', icon: ShoppingCart, badge: 23 },
  { id: 'map', label: '客户分布', icon: Map },
  { id: 'location', label: '选址对赌', icon: MapPin },
  { id: 'leads', label: 'OpenClaw', icon: Zap, badge: 12 },
];

const STORES = [
  { id: 'S001', name: '明亮眼镜（望京店）', owner: '张建国', phone: '138****8888', status: 'active', sales: 156000, orders: 45, area: '朝阳区', wxSales: 98000, rdSales: 58000 },
  { id: 'S002', name: '视界眼镜（三里屯店）', owner: '李小红', phone: '139****6666', status: 'active', sales: 128000, orders: 38, area: '朝阳区', wxSales: 85000, rdSales: 43000 },
  { id: 'S003', name: '睛彩眼镜（通州店）', owner: '王大海', phone: '137****9999', status: 'warning', sales: 45000, orders: 12, area: '通州区', wxSales: 35000, rdSales: 10000 },
  { id: 'S004', name: '光明眼镜（海淀店）', owner: '赵小燕', phone: '136****7777', status: 'new', sales: 0, orders: 0, area: '海淀区', wxSales: 0, rdSales: 0 },
  { id: 'S005', name: '新视界（国贸店）', owner: '刘先生', phone: '135****5555', status: 'active', sales: 198000, orders: 52, area: '朝阳区', wxSales: 120000, rdSales: 78000 },
];

const CONTRACTS = [
  { id: 'C001', store: '明亮眼镜望京店', brand: '万新光学', type: '年度合作', value: 500000, start: '2024-01-01', end: '2024-12-31', status: 'active', rebate: 8 },
  { id: 'C002', store: '明亮眼镜望京店', brand: '罗敦司得', type: '年度合作', value: 300000, start: '2024-01-01', end: '2024-12-31', status: 'active', rebate: 12 },
  { id: 'C003', store: '视界眼镜三里屯店', brand: '万新光学', type: '年度合作', value: 400000, start: '2024-04-01', end: '2025-03-31', status: 'active', rebate: 7 },
  { id: 'C004', store: '睛彩眼镜通州店', brand: '万新光学', type: '季度合作', value: 100000, start: '2024-01-01', end: '2024-06-30', status: 'expiring', rebate: 5 },
];

const ORDERS = [
  { id: 'SO20240326001', store: '明亮眼镜望京店', brand: '万新光学', items: '1.67镜片*20', amount: 45800, status: 'shipped', date: '2024-03-26' },
  { id: 'SO20240325002', store: '视界眼镜三里屯店', brand: '罗敦司得', items: '渐进片*10', amount: 28600, status: 'processing', date: '2024-03-25' },
  { id: 'SO20240324003', store: '睛彩眼镜通州店', brand: '万新光学', items: '1.60镜片*50', amount: 12400, status: 'pending', date: '2024-03-24' },
];

// ==================== Logo组件 ====================
const Logo: React.FC = () => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-xl overflow-hidden shadow-brand">
      <svg viewBox="0 0 32 32" className="w-full h-full">
        <defs>
          <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#C41E3A" />
            <stop offset="50%" stopColor="#8B4789" />
            <stop offset="100%" stopColor="#4A90A4" />
          </linearGradient>
        </defs>
        <rect width="32" height="32" rx="8" fill="url(#logoGrad)" />
        <circle cx="10" cy="14" r="5" stroke="white" strokeWidth="2" fill="none" />
        <circle cx="22" cy="14" r="5" stroke="white" strokeWidth="2" fill="none" />
        <path d="M14 14 L18 14" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <circle cx="16" cy="7" r="2" fill="#228B22" />
      </svg>
    </div>
    <div>
      <h1 className="text-base font-bold text-gray-900">镜界 JINGJIE</h1>
      <p className="text-xs text-gray-500">LinkSight 智能眼镜管理平台</p>
    </div>
  </div>
);

// ==================== Dashboard模块 ====================
const DashboardModule: React.FC = () => {
  const stats = [
    { title: '本月营收', value: '¥856万', change: '+12.5%', up: true, icon: DollarSign },
    { title: '管辖门店', value: '156家', change: '+8家', up: true, icon: Store },
    { title: '活跃订单', value: '486单', change: '+23.2%', up: true, icon: ShoppingBag },
    { title: '待处理预警', value: '12项', change: '-3项', up: true, icon: AlertCircle },
  ];

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      {/* 头部 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">经营总览</h2>
          <p className="text-sm text-gray-500 mt-1">实时监控业务数据，洞察经营趋势</p>
        </div>
        <div className="flex bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
          {['本周', '本月', '本季', '本年'].map((t, i) => (
            <button
              key={t}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                i === 1 
                  ? 'gradient-brand text-white shadow-md' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* 核心数据卡片 */}
      <div className="grid grid-cols-4 gap-5">
        {stats.map((stat, i) => (
          <div key={i} className="card">
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl gradient-brand flex items-center justify-center shadow-brand">
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                stat.up ? 'bg-[rgba(34,139,34,0.1)] text-[#228B22]' : 'bg-[rgba(239,68,68,0.1)] text-[#EF4444]'
              }`}>
                {stat.up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {stat.change}
              </div>
            </div>
            <div className="mt-4">
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500 mt-0.5">{stat.title}</p>
            </div>
          </div>
        ))}
      </div>

      {/* 图表区域占位 */}
      <div className="grid grid-cols-3 gap-5">
        <div className="card h-64 flex items-center justify-center text-gray-400">
          万新光学销售趋势图
        </div>
        <div className="card h-64 flex items-center justify-center text-gray-400">
          罗敦司得销售趋势图
        </div>
        <div className="card h-64 flex items-center justify-center text-gray-400">
          月度销售额柱状图
        </div>
      </div>

      {/* 底部信息 */}
      <div className="grid grid-cols-3 gap-5">
        <div className="card">
          <h3 className="text-base font-bold text-gray-900 mb-4">客户类型分布</h3>
          <div className="h-32 flex items-center justify-center text-gray-400">环形图</div>
        </div>
        <div className="card">
          <h3 className="text-base font-bold text-gray-900 mb-4">区域销售占比</h3>
          <div className="h-32 flex items-center justify-center text-gray-400">条形图</div>
        </div>
        <div className="space-y-4">
          <div className="card py-4">
            <h3 className="text-base font-bold text-gray-900 mb-3">今日待办</h3>
            <div className="space-y-2">
              {['客户生日提醒', '合同即将到期', '销售异常预警'].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                  <div className="w-2 h-2 rounded-full bg-[#C41E3A]" />
                  <span className="text-sm text-gray-700 flex-1">{item}</span>
                  <span className="text-xs text-gray-500">{[3, 5, 8][i]}项</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== 门店管理 ====================
const StoresModule: React.FC = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredStores = STORES.filter(store => {
    if (filter !== 'all' && store.status !== filter) return false;
    if (searchTerm && !store.name.includes(searchTerm) && !store.owner.includes(searchTerm)) return false;
    return true;
  });

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      {/* 操作栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
            {[
              { id: 'all', label: '全部', count: STORES.length },
              { id: 'active', label: '活跃', count: STORES.filter(s => s.status === 'active').length },
              { id: 'warning', label: '预警', count: STORES.filter(s => s.status === 'warning').length },
              { id: 'new', label: '新入驻', count: STORES.filter(s => s.status === 'new').length },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === f.id 
                    ? 'bg-gray-900 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {f.label} ({f.count})
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜索门店/负责人"
              className="input w-64 pl-10"
            />
          </div>
        </div>
        <button className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          新增门店
        </button>
      </div>

      {/* 表格 */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">门店信息</th>
              <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">负责人</th>
              <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">本月销售</th>
              <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">品牌</th>
              <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">状态</th>
              <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredStores.map((store) => (
              <tr key={store.id} className="hover:bg-gray-50/50 transition-colors">
                <td className="py-4 px-6">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl gradient-brand flex items-center justify-center text-white font-bold">
                      {store.name[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{store.name}</p>
                      <p className="text-xs text-gray-500">{store.area}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <p className="text-sm font-medium text-gray-900">{store.owner}</p>
                  <p className="text-xs text-gray-500">{store.phone}</p>
                </td>
                <td className="py-4 px-6">
                  <p className="font-semibold text-gray-900">¥{store.sales.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">{store.orders} 单</p>
                </td>
                <td className="py-4 px-6">
                  <div className="flex gap-1.5">
                    {store.wxSales > 0 && <span className="px-2 py-1 bg-[rgba(196,30,58,0.1)] text-[#C41E3A] rounded-lg text-xs font-medium">WX</span>}
                    {store.rdSales > 0 && <span className="px-2 py-1 bg-[rgba(139,71,137,0.1)] text-[#8B4789] rounded-lg text-xs font-medium">RD</span>}
                  </div>
                </td>
                <td className="py-4 px-6">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                    store.status === 'active' ? 'badge-success' :
                    store.status === 'warning' ? 'badge-error' :
                    'bg-blue-50 text-blue-700 border border-blue-200'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      store.status === 'active' ? 'bg-[#228B22]' :
                      store.status === 'warning' ? 'bg-[#EF4444]' :
                      'bg-blue-500'
                    }`} />
                    {store.status === 'active' ? '活跃' : store.status === 'warning' ? '预警' : '新入驻'}
                  </span>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center gap-1">
                    <button className="p-2 text-gray-400 hover:text-[#C41E3A] hover:bg-[rgba(196,30,58,0.1)] rounded-lg transition-colors">
                      <Phone className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-[#228B22] hover:bg-[rgba(34,139,34,0.1)] rounded-lg transition-colors">
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-[#8B4789] hover:bg-[rgba(139,71,137,0.1)] rounded-lg transition-colors">
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== 合同管理 ====================
const ContractsModule: React.FC = () => (
  <div className="p-8 max-w-7xl mx-auto space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex gap-2">
        {['全部协议', '万新光学', '罗敦司得', '即将到期'].map((f) => (
          <button key={f} className="btn-secondary">
            {f}
          </button>
        ))}
      </div>
      <button className="btn-primary flex items-center gap-2">
        <Plus className="w-4 h-4" />
        新建协议
      </button>
    </div>

    <div className="grid gap-4">
      {CONTRACTS.map((contract) => (
        <div key={contract.id} className="card">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
                  contract.brand === '万新光学' ? 'bg-[rgba(196,30,58,0.1)] text-[#C41E3A]' : 'bg-[rgba(139,71,137,0.1)] text-[#8B4789]'
                }`}>
                  {contract.brand === '万新光学' ? 'WX' : 'RD'}
                </span>
                <h4 className="font-semibold text-gray-900">{contract.type}</h4>
                <span className={`badge-${contract.status === 'expiring' ? 'warning' : 'success'}`}>
                  {contract.status === 'expiring' ? '即将到期' : '进行中'}
                </span>
              </div>
              <p className="text-sm text-gray-500">{contract.store}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">¥{contract.value.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">返利 {contract.rebate}%</p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-5 pt-5 border-t border-gray-100">
            <div className="flex gap-6 text-sm text-gray-500">
              <span>起: <span className="text-gray-700 font-medium">{contract.start}</span></span>
              <span>止: <span className="text-gray-700 font-medium">{contract.end}</span></span>
            </div>
            <div className="flex gap-3">
              <button className="px-4 py-2 text-sm text-gray-600 hover:text-[#C41E3A] transition-colors">
                查看
              </button>
              {contract.status === 'expiring' && (
                <button className="px-4 py-2 bg-[#F59E0B] text-white text-sm rounded-xl hover:opacity-90 transition-opacity">
                  续签
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ==================== 订单管理 ====================
const OrdersModule: React.FC = () => (
  <div className="p-8 max-w-7xl mx-auto space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex gap-4">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-xl">
          <Package className="w-5 h-5 text-blue-600" />
          <span className="text-sm text-gray-700">待确认 <strong className="text-blue-600">3</strong></span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-100 rounded-xl">
          <Clock className="w-5 h-5 text-amber-600" />
          <span className="text-sm text-gray-700">待发货 <strong className="text-amber-600">5</strong></span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 bg-purple-50 border border-purple-100 rounded-xl">
          <Truck className="w-5 h-5 text-purple-600" />
          <span className="text-sm text-gray-700">运输中 <strong className="text-purple-600">8</strong></span>
        </div>
      </div>
      <button className="btn-secondary flex items-center gap-2">
        <Download className="w-4 h-4" />
        导出
      </button>
    </div>

    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">订单号</th>
            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">门店</th>
            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">品牌</th>
            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">金额</th>
            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">状态</th>
            <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">操作</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {ORDERS.map((order) => (
            <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
              <td className="py-4 px-6 font-mono text-sm text-gray-600">{order.id}</td>
              <td className="py-4 px-6 text-sm font-medium text-gray-900">{order.store}</td>
              <td className="py-4 px-6">
                <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${
                  order.brand === '万新光学' ? 'bg-[rgba(196,30,58,0.1)] text-[#C41E3A]' : 'bg-[rgba(139,71,137,0.1)] text-[#8B4789]'
                }`}>
                  {order.brand === '万新光学' ? 'WX' : 'RD'}
                </span>
              </td>
              <td className="py-4 px-6 font-semibold text-gray-900">¥{order.amount.toLocaleString()}</td>
              <td className="py-4 px-6">
                <span className={`badge-${order.status === 'shipped' ? 'success' : order.status === 'processing' ? 'info' : 'warning'}`}>
                  {order.status === 'shipped' ? '已发货' : order.status === 'processing' ? '处理中' : '待确认'}
                </span>
              </td>
              <td className="py-4 px-6">
                <button className="text-[#C41E3A] text-sm font-medium hover:opacity-80 transition-opacity">
                  处理 →
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ==================== 销售分析 ====================
const SalesModule: React.FC = () => (
  <div className="p-8 max-w-7xl mx-auto space-y-6">
    <div className="grid grid-cols-3 gap-5">
      <div className="card">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-gray-500 text-sm">本月销售额</p>
            <p className="text-2xl font-bold text-gray-900">¥856万</p>
          </div>
        </div>
        <p className="text-sm text-[#228B22] flex items-center gap-1">
          <TrendingUp className="w-4 h-4" /> +12.5% 环比
        </p>
      </div>
      <div className="card">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <Target className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="text-gray-500 text-sm">平均客单价</p>
            <p className="text-2xl font-bold text-gray-900">¥1,280</p>
          </div>
        </div>
        <p className="text-sm text-[#EF4444] flex items-center gap-1">
          <TrendingDown className="w-4 h-4" /> -3.2% 环比
        </p>
      </div>
      <div className="card">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
            <Award className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-gray-500 text-sm">最佳单品</p>
            <p className="text-lg font-bold text-gray-900">万新1.67</p>
          </div>
        </div>
        <p className="text-sm text-gray-500">销量 1,256 片</p>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-5">
      <div className="card">
        <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-500" />
          门店销售排行
        </h3>
        <div className="space-y-3">
          {STORES.sort((a,b) => b.sales - a.sales).map((store, i) => (
            <div key={store.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                i === 0 ? 'bg-yellow-100 text-yellow-700' :
                i === 1 ? 'bg-gray-200 text-gray-700' :
                i === 2 ? 'bg-orange-100 text-orange-700' :
                'bg-gray-100 text-gray-500'
              }`}>
                {i + 1}
              </div>
              <span className="flex-1 text-sm text-gray-900">{store.name}</span>
              <span className="font-bold text-gray-900">¥{(store.sales/10000).toFixed(1)}万</span>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-purple-500" />
          品类销售占比
        </h3>
        <div className="space-y-4">
          {[
            { name: '镜片', percent: 45, color: 'bg-blue-500' },
            { name: '镜架', percent: 30, color: 'bg-purple-500' },
            { name: '隐形眼镜', percent: 15, color: 'bg-green-500' },
            { name: '护理产品', percent: 10, color: 'bg-orange-500' },
          ].map((item) => (
            <div key={item.name}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600">{item.name}</span>
                <span className="font-medium">{item.percent}%</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.percent}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ==================== 主组件 ====================

export const AgentSystemFinal: React.FC = () => {
  const [activeModule, setActiveModule] = useState('dashboard');
  const [selectedBrand, setSelectedBrand] = useState('all');

  const renderModule = () => {
    switch(activeModule) {
      case 'dashboard': return <DashboardModule />;
      case 'stores': return <StoresModule />;
      case 'contracts': return <ContractsModule />;
      case 'orders': return <OrdersModule />;
      case 'sales': return <SalesModule />;
      case 'map': return <ClientMapModule />;
      case 'location': return <LocationScoutModule />;
      case 'leads': return <OpenClawModule />;
      default: return <DashboardModule />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex font-sans antialiased">
      {/* 侧边栏 */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-gray-200 flex flex-col z-50">
        {/* Logo */}
        <div className="h-20 flex items-center px-6 border-b border-gray-100">
          <Logo />
        </div>

        {/* 品牌切换 */}
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">代理品牌</p>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedBrand('all')}
              className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold transition-all ${
                selectedBrand === 'all' 
                  ? 'bg-gray-900 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
              `}
            >
              全部
            </button>
            {AGENT_INFO.brands.map(b => (
              <button
                key={b.id}
                onClick={() => setSelectedBrand(b.id)}
                className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                  selectedBrand === b.id 
                    ? b.id === 'wx' 
                      ? 'gradient-brand text-white shadow-md' 
                      : 'bg-[#8B4789] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                `}
              >
                {b.logo}
              </button>
            ))}
          </div>
        </div>

        {/* 导航菜单 */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {MODULES.map((module) => {
            const isActive = activeModule === module.id;
            const Icon = module.icon;
            
            return (
              <button
                key={module.id}
                onClick={() => setActiveModule(module.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 group ${
                  isActive 
                    ? 'nav-item-active' 
                    : 'nav-item'}
                `}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                  isActive 
                    ? 'gradient-brand text-white shadow-md' 
                    : 'bg-gray-100 text-gray-500 group-hover:bg-white group-hover:shadow-sm'}
                `}>
                  <Icon className="w-4.5 h-4.5" />
                </div>
                <span className="flex-1 font-medium text-sm">{module.label}</span>
                {module.badge && (
                  <span className="min-w-[20px] h-5 px-1.5 gradient-brand text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {module.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* 用户区 */}
        <div className="p-4 border-t border-gray-100">
          <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
            <div className="w-10 h-10 rounded-full gradient-brand flex items-center justify-center text-white text-sm font-bold shadow-brand">
              张
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-gray-900">张总</p>
              <p className="text-xs text-gray-500">{AGENT_INFO.region}</p>
            </div>
            <div className="w-2.5 h-2.5 bg-[#228B22] rounded-full border-2 border-white shadow-sm" />
          </button>
        </div>
      </aside>

      {/* 主内容区 */}
      <main className="flex-1 flex flex-col min-w-0 ml-64">
        {/* 顶部导航 */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8 sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold text-gray-900">
              {MODULES.find(m => m.id === activeModule)?.label}
            </h2>
            {selectedBrand !== 'all' && (
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                selectedBrand === 'wx' 
                  ? 'bg-[rgba(196,30,58,0.1)] text-[#C41E3A] border-[rgba(196,30,58,0.2)]' 
                  : 'bg-[rgba(139,71,137,0.1)] text-[#8B4789] border-[rgba(139,71,137,0.2)]'}
              `}>
                {AGENT_INFO.brands.find(b => b.id === selectedBrand)?.name}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input 
                type="text" 
                placeholder="搜索..."
                className="input w-64 pl-10"
              />
            </div>
            <button className="relative p-2.5 text-gray-500 hover:text-[#C41E3A] hover:bg-[rgba(196,30,58,0.1)] rounded-xl transition-all">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-[#C41E3A] rounded-full border-2 border-white" />
            </button>
            <button className="p-2.5 text-gray-500 hover:text-[#8B4789] hover:bg-[rgba(139,71,137,0.1)] rounded-xl transition-all">
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* 内容区域 */}
        <div className="flex-1 overflow-auto">
          {renderModule()}
        </div>
      </main>
    </div>
  );
};

export default AgentSystemFinal;
